/** A small key-value store in IndexedDB for the data that a phone keeps for offline use. */

import { forgetShared } from "./share";

const NAME = "xflow-offline";
const STORE = "kv";

export interface Cached<T> {
  at: number;
  value: T;
}

let opened: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  opened ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  }).catch((error) => {
    opened = null;
    throw error;
  });
  return opened;
}

function run<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  return open().then(
    (db) =>
      new Promise<T | undefined>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const request = work(tx.objectStore(STORE));
        tx.oncomplete = () => resolve(request ? request.result : undefined);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      }),
  );
}

/** Null when the key is missing or the browser has no IndexedDB. */
export async function read<T>(key: string): Promise<Cached<T> | null> {
  try {
    return ((await run<Cached<T>>("readonly", (store) => store.get(key))) as Cached<T> | undefined) ?? null;
  } catch {
    return null;
  }
}

export async function write<T>(key: string, value: T, at: number = Date.now()): Promise<void> {
  try {
    await run("readwrite", (store) => store.put({ at, value } satisfies Cached<T>, key));
  } catch {
    // Without storage, the app works online only.
  }
}

export async function keys(prefix: string): Promise<string[]> {
  try {
    const range = IDBKeyRange.bound(prefix, `${prefix}￿`);
    return ((await run<IDBValidKey[]>("readonly", (store) => store.getAllKeys(range))) ?? []).map(String);
  } catch {
    return [];
  }
}

/** Read and write in one transaction, so that no tab writes between them. False when `keep` keeps the old value. */
export async function writeUnless<T>(key: string, value: T, keep: (old: T) => boolean, at: number = Date.now()): Promise<boolean> {
  let written = false;
  try {
    await run("readwrite", (store) => {
      const found = store.get(key);
      found.onsuccess = () => {
        const old = found.result as Cached<T> | undefined;
        if (old && keep(old.value)) return;
        written = true;
        store.put({ at, value } satisfies Cached<T>, key);
      };
    });
  } catch {
    return false;
  }
  return written;
}

export async function remove(names: string[]): Promise<void> {
  if (!names.length) return;
  try {
    await run("readwrite", (store) => {
      for (const name of names) store.delete(name);
    });
  } catch {
    // The next prune tries again.
  }
}

export const bootstrapKey = (slug: string) => `bootstrap:${slug}`;

/** The keys of one user in one organization, so that two users on one phone never share data. */
export function userScope(slug: string, uid: string) {
  const prefix = `user:${slug}:${uid}:`;
  return {
    prefix,
    key: (name: string) => `${prefix}${name}`,
    read: <T>(name: string) => read<T>(`${prefix}${name}`),
    write: <T>(name: string, value: T, at?: number) => write(`${prefix}${name}`, value, at),
  };
}

export type Scope = ReturnType<typeof userScope>;

/** Remove the day records of the days before `today`; ISO days sort as text. */
export async function pruneDays(scope: Scope, today: string): Promise<void> {
  const old = (await keys(scope.prefix)).filter((key) => {
    const day = /:(?:route|loads|ready):(\d{4}-\d{2}-\d{2})$/.exec(key)?.[1];
    return day !== undefined && day < today;
  });
  await remove(old);
}

/** For tests: forget the open connection after the IndexedDB factory changes. */
export function resetForTests(): void {
  opened = null;
  forgetShared();
}
